import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export default function Sidebar({ onNavigate }) {
  const location = useLocation();
  const { user }   = useAuth();
  const isAdmin    = user?.role === 'Admin';

  const isActive = (path, opts = {}) => {
    if (opts.exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleClick = () => { if (onNavigate) onNavigate(); };

  return (
    <aside style={s.sidebar}>
      <nav style={s.nav}>
        <Section title="General">
          <NavItem to="/"           label="Home"       icon={<HomeIcon />}      active={isActive('/', { exact: true })} onClick={handleClick} />
          <NavItem to="/stocks"     label="Stocks"     icon={<TrendingIcon />}  active={isActive('/stocks')}            onClick={handleClick} />
          <NavItem to="/crypto"     label="Crypto"     icon={<CoinIcon />}      active={isActive('/crypto')}            onClick={handleClick} />
          <NavItem to="/portfolios" label="Portfolios" icon={<BriefcaseIcon />} active={isActive('/portfolios') || location.pathname.startsWith('/portfolio/')} onClick={handleClick} />
          <NavItem to="/profile"    label="Profile"    icon={<UserIcon />}      active={isActive('/profile')}           onClick={handleClick} />
          {user && (
            <NavItem to="/settings" label="Settings"   icon={<GearIcon />}      active={isActive('/settings')}          onClick={handleClick} />
          )}
        </Section>

        <div style={s.bottomGroup}>
          <Section title="More">
            <NavItem to="/legal" label="Legal" icon={<DocIcon />} active={isActive('/legal') || isActive('/privacy') || isActive('/terms') || isActive('/cookies') || isActive('/subprocessors')} onClick={handleClick} />
          </Section>

          {isAdmin && (
            <Section title="Admin tools">
              <NavItem to="/admin"          label="Statistics"      icon={<ChartIcon />}    active={isActive('/admin', { exact: true })} onClick={handleClick} accent />
              <NavItem to="/admin/landing"  label="Landing preview" icon={<GlobeIcon />}    active={isActive('/admin/landing')}          onClick={handleClick} accent />
              <NavItem to="/admin/roadmap"  label="Roadmap editor"  icon={<MapIcon />}      active={isActive('/admin/roadmap')}          onClick={handleClick} accent />
              <NavItem to="/admin/progress" label="Progress"        icon={<ProgressIcon />} active={isActive('/admin/progress')}         onClick={handleClick} accent />

              <SubSection title="In Development">
                <NavItem to="/commodities" label="Commodities" icon={<CommoditiesIcon />} active={isActive('/commodities')} onClick={handleClick} accent />
              </SubSection>
            </Section>
          )}
        </div>
      </nav>
    </aside>
  );
}

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      <div style={s.sectionList}>{children}</div>
    </div>
  );
}

function SubSection({ title, children }) {
  return (
    <div style={s.subSection}>
      <div style={s.subSectionTitle}>{title}</div>
      <div style={s.sectionList}>{children}</div>
    </div>
  );
}

function NavItem({ to, label, icon, active, accent, onClick }) {
  // Labels always use the normal text colour — no purple text. Active items get
  // a soft pill background, a left accent bar and a tinted icon instead.
  const style = {
    ...s.item,
    ...(active ? s.itemActive : {}),
  };
  const iconStyle = {
    ...s.icon,
    color: active ? 'var(--primary)' : 'var(--text-secondary)',
  };
  return (
    <Link to={to} style={style} onClick={onClick}>
      <span style={iconStyle}>{icon}</span>
      <span>{label}</span>
    </Link>
  );
}

function HomeIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9.5L12 3l9 6.5V21a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1z"/></svg>;
}
function BriefcaseIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>;
}
function UserIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
}
function GearIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.16.38.5.66.92.74H21a2 2 0 1 1 0 4h-.09c-.42.08-.76.36-.92.74z"/></svg>;
}
function TrendingIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 17 9 11 13 15 21 7"/><polyline points="14 7 21 7 21 14"/></svg>;
}
function CoinIcon() {
  // Bitcoin ₿ glyph — same as the homepage Crypto card, scaled down
  return (
    <svg width="18" height="18" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="32" cy="32" r="23" />
      <path d="M26 20v24" />
      <path d="M26 20h9a6 6 0 0 1 0 12h-9" />
      <path d="M26 32h10.5a6 6 0 0 1 0 12H26" />
      <line x1="30" y1="16" x2="30" y2="20" />
      <line x1="36" y1="16" x2="36" y2="20" />
      <line x1="30" y1="44" x2="30" y2="48" />
      <line x1="36" y1="44" x2="36" y2="48" />
    </svg>
  );
}
function CommoditiesIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a7 7 0 0 1 7 7c0 5-7 13-7 13S5 14 5 9a7 7 0 0 1 7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>;
}
function ChartIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="20" x2="21" y2="20"/><rect x="5" y="10" width="3" height="10"/><rect x="11" y="6" width="3" height="14"/><rect x="17" y="13" width="3" height="7"/></svg>;
}
function GlobeIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><line x1="3" y1="12" x2="21" y2="12"/><path d="M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></svg>;
}
function DocIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="14" y2="17"/></svg>;
}
function ProgressIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>;
}
function MapIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 6v16l7-3 8 3 7-3V3l-7 3-8-3z"/><line x1="8" y1="3" x2="8" y2="19"/><line x1="16" y1="6" x2="16" y2="22"/></svg>;
}

const s = {
  sidebar:           { width: '220px', flexShrink: 0, padding: '16px 8px', borderRight: '1px solid var(--border)', background: 'var(--surface)', height: 'calc(100vh - 53px)', boxSizing: 'border-box', position: 'sticky', top: '53px', alignSelf: 'flex-start', overflowY: 'auto' },
  nav:               { display: 'flex', flexDirection: 'column', gap: '20px', minHeight: '100%' },
  bottomGroup:       { marginTop: 'auto', paddingTop: '20px' },
  section:           { display: 'flex', flexDirection: 'column', gap: '6px' },
  sectionTitle:      { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--text-secondary)', padding: '0 10px', marginBottom: '2px' },
  sectionList:       { display: 'flex', flexDirection: 'column', gap: '2px' },
  subSection:        { display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '10px', paddingLeft: '6px', borderLeft: '2px solid var(--border)', marginLeft: '8px' },
  subSectionTitle:   { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-secondary)', padding: '0 8px', opacity: 0.75 },
  item:              { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '10px', textDecoration: 'none', color: 'var(--text-primary)', fontSize: '14px', fontWeight: 600, transition: 'background 0.12s', border: '1px solid transparent' },
  itemActive:        { background: 'rgba(99,102,241,0.14)', color: 'var(--primary)', fontWeight: 700, border: '1px solid rgba(99,102,241,0.30)' },
  icon:              { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px' },
};
